import { DEFAULT_LANGUAGE, LANGUAGES, SupportedLanguage, useLanguage, useTranslation } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import clientStorage from "@next-platform/client-common-core/services/clientStorage";
import { useCallback, useEffect, useState } from "react";

import { USER_LANGUAGE_PREFERENCE_LOCAL_STORAGE_KEY } from "@/constants";

import useIsMultiLangEnabled from "./useIsMultiLangEnabled";
import useLocalizedLocation from "./useLocalizedLocation";
import useLocalizedNavigation from "./useLocalizedNavigation";

const autoLanguageSelectLogger = appLogger.withTag("AutoLanguageSelect");

type UseAutoLanguageSelectReturn = {
  isChangingLanguage: boolean;
};

type ReplaceUrlAndSyncLanguageParams = {
  changeLanguage: (targetLanguage: SupportedLanguage) => void;
  currentLanguage: SupportedLanguage;
  navigate: ReturnType<typeof useLocalizedNavigation>;
  pagePath: string;
  search: string;
  targetLanguage: SupportedLanguage;
};

const readStoredLanguagePreference = (): Promise<string | null> =>
  clientStorage.getItem(USER_LANGUAGE_PREFERENCE_LOCAL_STORAGE_KEY).catch((readError: unknown) => {
    autoLanguageSelectLogger.error("Failed to read the stored language preference:", readError);
    return null;
  });

const replaceUrlAndSyncLanguage = ({
  changeLanguage,
  currentLanguage,
  navigate,
  pagePath,
  search,
  targetLanguage,
}: ReplaceUrlAndSyncLanguageParams) => {
  navigate({ pathname: pagePath, search }, { replace: true }, targetLanguage);
  if (currentLanguage !== targetLanguage) {
    changeLanguage(targetLanguage);
  }
};

const useAutoLangugeSelect = (): UseAutoLanguageSelectReturn => {
  const { isMultiLangEnabled } = useIsMultiLangEnabled();
  const { pagePath, languageCode: languageCodeInUrl, search, isSupportedLang } = useLocalizedLocation();
  const navigate = useLocalizedNavigation();
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language as SupportedLanguage;
  const { setCurrentLanguage } = useLanguage();
  const [stateIsChangingLanguage, setStateIsChangingLanguage] = useState(false);

  const changeLanguage = useCallback(
    (targetLanguage: SupportedLanguage) => {
      setStateIsChangingLanguage(true);
      setCurrentLanguage(targetLanguage)
        .catch((changeError: unknown) => {
          autoLanguageSelectLogger.error("Failed to change language:", changeError);
        })
        .finally(() => {
          setStateIsChangingLanguage(false);
        });
    },
    [setCurrentLanguage],
  );

  const applyStoredLanguagePreference = useCallback(
    (storedLanguage: string | null) => {
      const isLanguageMissingInUrl = !languageCodeInUrl;
      const isStoredLangSupported = LANGUAGES[storedLanguage as SupportedLanguage];
      if (isLanguageMissingInUrl && isStoredLangSupported) {
        if (currentLanguage !== storedLanguage) {
          changeLanguage(storedLanguage as SupportedLanguage);
        }
        if (storedLanguage !== DEFAULT_LANGUAGE) {
          navigate({ pathname: pagePath, search }, { replace: true }, storedLanguage as SupportedLanguage);
        }
      } else if (languageCodeInUrl && isSupportedLang) {
        const userPreferredLanguage = storedLanguage as SupportedLanguage | null;
        const hasExplicitPreference = userPreferredLanguage && isStoredLangSupported;
        if (hasExplicitPreference && languageCodeInUrl !== userPreferredLanguage) {
          replaceUrlAndSyncLanguage({
            changeLanguage,
            currentLanguage,
            navigate,
            pagePath,
            search,
            targetLanguage: userPreferredLanguage,
          });
        } else if (currentLanguage !== languageCodeInUrl) {
          changeLanguage(languageCodeInUrl as SupportedLanguage);
        }
      } else if (languageCodeInUrl && !isSupportedLang) {
        // The URL segment isn't a real language (e.g. an invalid route like "/bb"), so it can't express a
        // language preference. Fall back to the default language instead of leaving a stale one in place.
        if (currentLanguage !== DEFAULT_LANGUAGE) {
          changeLanguage(DEFAULT_LANGUAGE);
        }
      }
    },
    [changeLanguage, currentLanguage, isSupportedLang, languageCodeInUrl, navigate, pagePath, search],
  );

  useEffect(() => {
    if (!isMultiLangEnabled) return;
    let isStale = false;

    readStoredLanguagePreference()
      .then((storedLanguage) => {
        if (isStale) return;
        applyStoredLanguagePreference(storedLanguage);
      })
      .catch((applyError: unknown) => {
        autoLanguageSelectLogger.error("Failed to apply language preference:", applyError);
      });

    return () => {
      isStale = true;
    };
  }, [applyStoredLanguagePreference, isMultiLangEnabled]);

  return { isChangingLanguage: stateIsChangingLanguage };
};

export default useAutoLangugeSelect;
